import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Download, Field, Skeleton, TextArea } from '@/components/ui';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { api } from '@/api/client';
import { DocumentView } from '@/features/report/DocumentView';
import { useBehaviourQuery } from './useBehaviourQuery';

const MIN_CHARS = 10;
const MAX_CHARS = 400;

export interface AskBehaviourCardProps {
  assessmentId: string;
}

/**
 * The one behaviour question included with an unlocked session: a single text box, then one plan
 * built from the saved answers and the question. Renders nothing while the session is locked.
 */
export const AskBehaviourCard = ({ assessmentId }: AskBehaviourCardProps) => {
  const t = useT();
  const { config } = useSession();
  const { view, loading, asking, error, ask } = useBehaviourQuery(assessmentId);
  const [text, setText] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [showPlan, setShowPlan] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // After a safety stop the box would still hold the parent's most upsetting words; start clean.
  const status = view?.status;
  useEffect(() => {
    if (status === 'escalated') {
      setText('');
      setAttempted(false);
    }
  }, [status]);

  // Nothing while loading: on deployments without the plan service the card never appears, so a
  // skeleton would only flash and collapse on every unlocked report.
  if (loading) return null;
  if (!view || view.status === 'locked' || view.status === 'unavailable') return null;

  const trimmed = text.trim();
  const tooShort = attempted && trimmed.length < MIN_CHARS;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (trimmed.length < MIN_CHARS) return;
    void ask(trimmed);
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      const { url } = await api.assessments.behaviourDownload(assessmentId);
      window.open(url, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  const form = (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <Field
        id="behaviour-query"
        label={t('behaviour.label')}
        hint={t('behaviour.examples')}
        error={tooShort ? t('behaviour.tooShort') : undefined}
      >
        <TextArea value={text} maxLength={MAX_CHARS} rows={4} onChange={(e) => setText(e.target.value)} />
      </Field>
      {error && <Alert tone="warn">{t(error)}</Alert>}
      <Button type="submit" loading={asking} full>
        {t('behaviour.cta')}
      </Button>
      <p className="text-caption text-ink-muted">{t('behaviour.oneOnly')}</p>
    </form>
  );

  if (view.status === 'generating') {
    return (
      <Card padding="lg">
        <h2 className="text-h2 text-ink">{t('behaviour.title')}</h2>
        <p className="mt-3 text-body text-ink-soft">
          {t('behaviour.yourQuestion')}: “{view.query}”
        </p>
        <div className="mt-4">
          <Skeleton lines={3} />
        </div>
        <p className="mt-4 text-caption text-ink-muted" role="status">
          {t('behaviour.generating')}
        </p>
      </Card>
    );
  }

  if (view.status === 'answered') {
    return (
      <Card padding="lg">
        <h2 className="text-h2 text-ink">{t('behaviour.planTitle')}</h2>
        <p className="mt-2 text-body text-ink-soft">
          {t('behaviour.yourQuestion')}: “{view.query}”
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" aria-expanded={showPlan} onClick={() => setShowPlan((open) => !open)}>
            {showPlan ? t('behaviour.hide') : t('behaviour.show')}
          </Button>
          <Button variant="secondary" loading={downloading} onClick={() => void onDownload()}>
            <Download className="mr-2 h-5 w-5" />
            {t('behaviour.download')}
          </Button>
        </div>
        {showPlan && (
          <div className="mt-6">
            {view.plan.watermark && (
              <Card tone="apricot" className="mb-4">
                <p className="text-body">{t('programme.provisional')}</p>
              </Card>
            )}
            <DocumentView document={view.plan} />
          </div>
        )}
        {config?.plans.plus && (
          <p className="mt-6 text-caption text-ink-muted">
            {t('behaviour.more')}{' '}
            <Link to="/plus" className="text-teal-600 underline underline-offset-4">
              {t('behaviour.morePlus')}
            </Link>
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card padding="lg">
      {view.status === 'escalated' ? (
        <>
          <h2 className="text-h2 text-ink">{t('behaviour.safety.title')}</h2>
          <Card tone="apricot" className="mt-3">
            <p className="text-body">{view.message}</p>
          </Card>
          <p className="mb-4 mt-4 text-body text-ink-soft">{t('behaviour.safety.again')}</p>
        </>
      ) : (
        <>
          <h2 className="text-h2 text-ink">{t('behaviour.title')}</h2>
          <p className="mb-4 mt-2 text-body text-ink-soft">{t('behaviour.hint')}</p>
          {view.failed && (
            <div className="mb-4">
              <Alert tone="warn">{t('behaviour.failed')}</Alert>
            </div>
          )}
        </>
      )}
      {form}
    </Card>
  );
};
